<template>
  <div>
    <!-- hack needed to autopopulate fields -->
    <input type="hidden" :value="inputValue" />
    <VcInput
      v-model="inputValue"
      :for="formElement.key"
      :label="getLabel()"
      :name="formElement.key"
      :placeholder="formElement.properties.placeHolder"
      :type="getType()"
      :required="isRequired"
      autocomplete="off"
      :message="message"
      :error="error"
      @input="validate"
    >
      <template #label="{ forId }">
        <label :for="forId" :class="['vc-label']">
          <div v-html-safe="getLabel()" class="inline" />
          <span v-if="isRequired" class="vc-label__asterisk">*</span>
        </label>
      </template>
    </VcInput>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { punchoutCookies } from "@/core/utilities/punchout/punchoutCookies";
import { useUser } from "@/shared/account";
import { useUserAddresses } from "@/shared/account/composables/useUserAddresses";
import { FormElementsContentTypes, LabelPlaceholders, TextAutoCompleteTypes } from "../enums";
import { FormValidator } from "../forms-sdk";
import type { ElementValidationResult } from "../forms-sdk";
import type { ExtendedFormElement } from "../types";
import type { Product } from "@/core/api/graphql/types";

interface IProps {
  formElement: ExtendedFormElement;
  product?: Product;
  errorMessage?: ElementValidationResult;
}

interface IEmits {
  (event: "update:error-message", error: ElementValidationResult): void;
}
const emit = defineEmits<IEmits>();

const props = defineProps<IProps>();
const inputValue = ref<string>(getValue() ?? "");
let isEditedByUser = false;
const elementValidationResult = ref<ElementValidationResult>({ valid: true, message: "" });
const message = computed(() => elementValidationResult.value?.message || props.errorMessage?.message);
const error = computed(() => !!elementValidationResult.value?.message || !!props.errorMessage?.message);

function getLabel(): string {
  let label = props.formElement.properties.label;
  const regex = /{{(.*)}}/;

  if (label) {
    regex.exec(label)?.forEach((placeholder) => {
      switch (placeholder) {
        case LabelPlaceholders.ItemNumber:
          label = label.replace(regex, props.product?.code ?? "");
          break;
        case "":
          break;
        default:
      }
    });
  }

  return label;
}

function getType(): "number" | "text" {
  return props.formElement.contentType === FormElementsContentTypes.NumberElementBlock ? "number" : "text";
}

const isRequired = computed(
  () => !!props.formElement.properties.validators.find((v) => v.type === "RequiredValidator"),
);

function getValue(): string | undefined {
  switch (props.formElement.properties.autoComplete) {
    case TextAutoCompleteTypes.Email: {
      if (punchoutCookies.value) {
        return "";
      }
      const { user } = useUser();
      return user.value?.email;
    }
    case TextAutoCompleteTypes.Name: {
      const { displayName } = useUser();
      return displayName.value;
    }
    case TextAutoCompleteTypes.FirstName: {
      const { user } = useUser();
      return user.value?.contact?.firstName;
    }
    case TextAutoCompleteTypes.LastName: {
      const { user } = useUser();
      return user.value?.contact?.lastName;
    }
    default:
      return props.formElement.properties.predefinedValue ?? "";
  }
}

// Company is not stored on the contact, so it is taken from the default billing address
async function prefillOrganization(): Promise<void> {
  if (props.formElement.properties.autoComplete !== (TextAutoCompleteTypes.Organization as string)) {
    return;
  }
  const { isAuthenticated } = useUser();
  // A CMS predefined value wins, so there is no need to load addresses
  if (!isAuthenticated.value || inputValue.value) {
    return;
  }

  const { addresses, fetchAddresses } = useUserAddresses();
  if (!addresses.value.length) {
    try {
      await fetchAddresses();
    } catch {
      // fetchAddresses already logs the error; the field simply stays empty
      return;
    }
  }

  const organization = addresses.value.find((address) => address.isDefaultBillingAddressTL)?.organization;
  // The user may have typed or cleared the field while addresses were loading
  if (organization && !isEditedByUser && !inputValue.value) {
    inputValue.value = organization;
    // Clears a "required" error shown if the user submitted before the prefill arrived
    elementValidationResult.value = formValidator.validate(organization);
    emit("update:error-message", elementValidationResult.value);
  }
}

const formValidator = new FormValidator(props.formElement);

void prefillOrganization();

function validate(event: Event): void {
  isEditedByUser = true;
  const input = event.target as HTMLInputElement;
  elementValidationResult.value = formValidator.validate(input.value);
  emit("update:error-message", elementValidationResult.value);
}
</script>
